/*eslint-disable*/
import React from "react";
import {
  useTable,
  useFilters,
  useSortBy,
  usePagination,
} from "react-table";
import classnames from "classnames";
// A great library for fuzzy filtering/sorting items
import matchSorter from "match-sorter";
// @material-ui/core components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
// core components
import CustomInput from "components/CustomInput/CustomInput";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Button from "components/CustomButtons/Button";
import BlueTypo from "components/Typography/Info";
import styles from "assets/jss/elite-feats-cms/customSelectStyle";
import dashboardStyles from "assets/jss/elite-feats-cms/views/dashboardStyle";
import allStyles from "assets/jss/elite-feats-cms/views/extendedTablesStyle";

import Grid from "@material-ui/core/Grid";
import { useTranslation } from "react-i18next";
import Checkbox from "@material-ui/core/Checkbox";
import Check from "@material-ui/icons/Check";
import ClientCardBase from "../Dashboard/ClientCard";
const ClientCard = ClientCardBase as React.ComponentType<{
  client: unknown;
  index: number;
  onToggle: (email: string) => void;
  onEdit: () => void;
  onAssessment: () => void;
  onEmail: () => void;
  isChecked?: unknown;
}>;

const newStyles = {
  ...styles,
  ...dashboardStyles,
  ...allStyles,

  formControlMargins: {
    margin: "3px 0 !important",
  },
  gridContainer: {
    justifyContent: "center",
  },
  tbcell: {
    display: "flex",
    alignItems: "center",
    paddingTop: 7,
    paddingBottom: 7,
    width: 50,
  },
  groupscell: {
    display: "flex",
    alignItems: "center",
    paddingTop: 7,
    paddingBottom: 7,
  },
  compcell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 110,
  },
  actionscell: {
    display: "flex",
    justifyContent: "flex-end",

    width: 160,
  },
  compheader: {
    display: "flex",
    alignItems: "center",
    marginTop: -50,
    width: 110,
  },
};

const useStyles = makeStyles(newStyles);

// Custom checkbox component that manages its own state and persists across pagination
const TableCheckbox = React.memo(
  ({ email, onToggle }: { email: string; onToggle: (email: string) => void }) => {
  const [isChecked, setIsChecked] = React.useState(() => 
    (JSON.parse(localStorage.getItem("selected_clients") || "[]") as string[]).includes(email)
  );

  // Listen for localStorage changes (from other checkboxes or clear selection)
  React.useEffect(() => {
    const handleStorageChange = () => {
      setIsChecked((JSON.parse(localStorage.getItem("selected_clients") || "[]") as string[]).includes(email));
    };

    // Check for changes periodically (since localStorage events don't fire within same page)
    const interval = setInterval(handleStorageChange, 100);
    
    return () => clearInterval(interval);
  }, [email]);

  const handleClick = () => {
    onToggle(email);
    // Update local state immediately for instant feedback
    setIsChecked(!isChecked);
  };

  const classes = useStyles() as Record<string, string>;

  return (
    <Checkbox
      style={{ padding: 7 }}
      checked={isChecked}
      onChange={handleClick}
      checkedIcon={<Check className={classes.checkedIcon} />}
      icon={<Check className={classes.uncheckedIcon} />}
      classes={{
        checked: classes.checkedGreen,
        root: classes.checkGreen,
      }}
    />
  );
});

function fuzzyTextFilterFn(
  rows: import("react-table").Row[],
  id: string,
  filterValue: string
) {
  return matchSorter(rows, filterValue, {
    keys: [(row: import("react-table").Row) => row.values[id]],
  });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val: unknown) => !val;

// Our table component
interface TableProps {
  columns: import("react-table").Column[];
  data: Record<string, unknown>[];
  clientTour?: unknown;
  onToggle?: (email: string) => void;
  isMobile?: boolean;
  setEdit?: (v: Record<string, unknown>) => void;
  setOpenEdit?: (v: boolean) => void;
  setAssignmentDetails?: (v: unknown) => void;
  setOpenAssigned?: (v: boolean) => void;
  setOpenClientMail?: (v: boolean) => void;
  setClientToMail?: (v: unknown) => void;
  tourRun?: boolean;
  triggerStep?: number;
  setTriggerStep?: (v: number) => void;
  reloadClients?: unknown;
}

function Table({
  columns,
  data,
  clientTour,
  onToggle,
  isMobile,
  setEdit,
  setOpenEdit,
  setAssignmentDetails,
  setOpenAssigned,
  setOpenClientMail,
  setClientToMail,
  tourRun,
  triggerStep,
  setTriggerStep,
}: TableProps): React.ReactElement {
  const { t } = useTranslation();
  const [numberOfRows, setNumberOfRows] = React.useState(10);
  const [pageSelect, handlePageSelect] = React.useState(0);
  const [searchValue, setSearchValue] = React.useState("");
  const [sortBy, setSortBy] = React.useState("first");
  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (!firstRender.current) {
      if (clientTour === -5) {
        setPageSize(5);
        setNumberOfRows(5);
      } else if (clientTour === -10) {
        /*    setPageSize(10);
        setNumberOfRows(10); */
      }
    } else {
      firstRender.current = false;
    }
  }, [clientTour]);

  React.useEffect(() => {}, [numberOfRows]);
  const classes = useStyles() as Record<string, string>;
  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (
      rows: import("react-table").Row[],
      id: string,
      filterValue: string
    ) => {
        return rows.filter((row: import("react-table").Row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter },
  }: {
    column: {
      filterValue: string;
      preFilteredRows: import("react-table").Row[];
      setFilter: (v: unknown) => void;
    };
  }) {
    const count = preFilteredRows.length;
  
    return (
      <CustomInput
        formControlProps={{
          fullWidth: true,
        }}
        inputProps={{
          value: filterValue || "",
          onChange: (e) => {
            setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
          },
          placeholder: t('clients:searchPlaceholder', { count }),
        }}
      />
    );
  }


  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    visibleColumns,
    nextPage,
    pageOptions,
    pageCount,
    previousPage,
    canPreviousPage,
    canNextPage,
    setPageSize,
    gotoPage,
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: { pageSize: 10, pageIndex: 0 },
    },
    useFilters, // useFilters!
    useSortBy,
    usePagination
  );

  // We don't want to render all of the rows for this example, so cap
  // it for this use case
  // const firstPageRows = rows.slice(0, 10);
  let pageSelectData = Array.apply(
    null,
    Array(pageOptions.length)
  ).map(function () {});
  let numberOfRowsData = [5, 10, 20, 25, 50, 100];

  // Filter and sort data for card view
  const getFilteredAndSortedData = () => {
    let filteredData = data;

    // Apply search filter
    if (searchValue) {
      filteredData = filteredData.filter((client: Record<string, unknown>) => {
        const searchLower = searchValue.toLowerCase();
        const first = (((client.first as string) || "")).toLowerCase();
        const last = (((client.last as string) || "")).toLowerCase();
        const email = (((client.email as string) || "")).toLowerCase();
        const groups = Array.isArray(client.group)
          ? (client.group as unknown[]).join(" ").toLowerCase()
          : "";

        return (
          first.includes(searchLower) ||
          last.includes(searchLower) ||
          email.includes(searchLower) ||
          groups.includes(searchLower)
        );
      });
    }

    // Apply sorting
    const sorted = [...filteredData].sort(
      (a: Record<string, unknown>, b: Record<string, unknown>) => {
      const aVal = ((a[sortBy] as string) || "").toString().toLowerCase();
      const bVal = ((b[sortBy] as string) || "").toString().toLowerCase();
      return aVal.localeCompare(bVal);
    });

    return sorted;
  };

  const filteredSortedData = isMobile ? getFilteredAndSortedData() : data;

  // Pagination for card view
  const [cardPageIndex, setCardPageIndex] = React.useState(0);
  const cardPageSize = numberOfRows;
  const cardPageCount = Math.ceil(filteredSortedData.length / cardPageSize);
  const cardPageData = isMobile
    ? filteredSortedData.slice(
        cardPageIndex * cardPageSize,
        (cardPageIndex + 1) * cardPageSize
      )
    : [];

  // Card view handlers
  const handleEditCard = (client: Record<string, unknown>) => {
    setEdit!(client);
    setOpenEdit!(true);
  };

  const handleAssessmentCard = (client: Record<string, unknown>) => {
    setAssignmentDetails!(client);
    setOpenAssigned!(true);
  };

  const handleEmailCard = (client: Record<string, unknown>) => {
    setClientToMail!(client);
    setOpenClientMail!(true);
  };

  // If mobile, render card view
  if (isMobile) {
    return (
      <>
        <div style={{ marginTop: 10, marginBottom: 10 }}>
          {/* Unified Search */}
          <Grid container spacing={2} style={{ marginBottom: 10 }}>
            <Grid item xs={12}>
              <CustomInput
                formControlProps={{
                  fullWidth: true,
                }}
                inputProps={{
                  value: searchValue,
                  onChange: (e) => setSearchValue(e.target.value),
                  placeholder: t("clients:searchPlaceholder", {
                    count: data.length,
                  }),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              {/* Sort Dropdown */}
              <FormControl fullWidth>
                <Select
                  value={sortBy}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                    setSortBy(e.target.value as string)
                  }
                  displayEmpty
                >
                  <MenuItem value="first">{t("clients:headerFirstName")}</MenuItem>
                  <MenuItem value="last">{t("clients:headerLastName")}</MenuItem>
                  <MenuItem value="email">{t("clients:headerEmail")}</MenuItem>
                  <MenuItem value="group">{t("clients:headerGroups")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Cards */}
          {cardPageData.length > 0 ? (
            cardPageData.map((client: Record<string, unknown>, index: number) => {
              const actualIndex = cardPageIndex * cardPageSize + index;
              const isChecked =
                JSON.parse(
                  localStorage.getItem("selected_clients") || "[]"
                ).includes(client.email);

              return (
                <ClientCard
                  key={actualIndex}
                  client={client}
                  index={actualIndex}
                  onToggle={onToggle!}
                  onEdit={() => handleEditCard(client)}
                  onAssessment={() => handleAssessmentCard((client.full as Record<string, unknown>) || client)}
                  onEmail={() => handleEmailCard((client.full as Record<string, unknown>) || client)}
                  isChecked={isChecked}
                />
              );
            })
          ) : (
            <Typography align="center" style={{ padding: 20 }}>
              {t("clients:noClientsFoundMessage")}
            </Typography>
          )}

          {/* Pagination */}
          <div className="pagination-top" style={{ marginTop: 20 }}>
            <div className="-pagination">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  {cardPageIndex > 0 && (
                    <Button
                      size="sm"
                      color="info"
                      fullWidth
                      onClick={() => setCardPageIndex(cardPageIndex - 1)}
                    >
                      {t("clients:pagination.previous")}
                    </Button>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography align="center">
                    {t("clients:pagination.page", {
                      number: cardPageIndex + 1,
                    })}{" "}
                    / {cardPageCount}
                  </Typography>
                  <FormControl fullWidth style={{ marginTop: 10 }}>
                    <Select
                      value={numberOfRows}
                      onChange={(event) => {
                        setNumberOfRows(event.target.value as number);
                        setCardPageIndex(0);
                      }}
                    >
                      {numberOfRowsData.map((prop) => (
                        <MenuItem key={prop} value={prop}>
                          {t("clients:pagination.rows", { count: prop })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  {cardPageIndex < cardPageCount - 1 && (
                    <Button
                      size="sm"
                      color="info"
                      fullWidth
                      onClick={() => setCardPageIndex(cardPageIndex + 1)}
                    >
                      {t("clients:pagination.nextPage")}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop: render table view
  return (
    <>
      <div className="ReactTable -striped -highlight">
        <table {...getTableProps()} className="rt-table">
          <thead className="rt-thead -header">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="rt-tr">
                {headerGroup.headers.map((column, key) => {
                  return (
                    <th
                      {...column.getHeaderProps()}
                      className={
                        column.id === "select"
                          ? classes.tbcell
                          : column.id === "completed"
                          ? classes.compheader
                          : column.id === "actions"
                          ? classes.actionscell
                          : classnames("rt-th rt-resizable-header")
                      }
                    >
                      <div
                        {...column.getSortByToggleProps()}
                        className={
                          column.id === "select"
                            ? classes.tbcell
                            : column.id === "completed"
                            ? classes.compcell
                            : column.id === "actions"
                            ? classes.actionscell
                            : classnames({
                                "-cursor-pointer":
                                  headerGroup.headers.length - 1 !== key,

                                "-sort-asc":
                                  column.isSorted && !column.isSortedDesc,

                                "-sort-desc":
                                  column.isSorted && column.isSortedDesc,
                              })
                        }
                        style={{
                          display: "flex",
                          justifyContent:
                            column.id === "completed" || column.id === "group"
                              ? "center"
                              : column.id === "actions"
                              ? "flex-end"
                              : undefined,
                          paddingRight:
                            column.id === "actions" ? 30 : undefined,
                        } as React.CSSProperties}
                      >
                        <Typography
                          style={{
                            display: "flex",
                            alignItems: "center",

                            fontSize: 18,
                            fontWeight: "bold",
                          }}
                        >
                          {" "}
                          {column.render("Header")}
                        </Typography>
                      </div>{" "}
                      {/* Render the columns filter UI */}
                      <div
                        style={{
                          marginTop:
                            column.id === "actions"
                              ? 0
                              : column.id === "completed"
                              ? 50
                              : -30,
                          maxWidth: column.id === "group" ? 200 : 150,
                          display: "flex",
                        }}
                      >
                        {headerGroup.headers.length - 1 === key
                          ? null
                          : column.id === "select" ||
                            column.id === "actions" ||
                            column.id === "completed"
                          ? null
                          : column.canFilter
                          ? column.render("Filter")
                          : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody
            {...getTableBodyProps()}
            className="rt-tbody"
            style={{
              marginTop: -15,
              maxHeight: 360,
              overflow: "auto !important",
              border: "1px solid #e9e9e9",
              borderRadius: 5,
            }}
          >
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  className={classnames(
                    "rt-tr",
                    { " -odd": i % 2 === 0 },
                    { " -even": i % 2 === 1 },
                    i === 0 ? "latest-client" : null
                  )}
                  style={{
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                >
                  {" "}
                  {row.cells.map((cell) => {
                    return (
                      <td
                        style={{
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        {...cell.getCellProps()}
                        className={
                          cell.column.id === "select"
                            ? classes.tbcell
                            : cell.column.id === "actions"
                            ? classes.actionscell
                            : cell.column.id === "completed"
                            ? classes.compcell
                            : "rt-td"
                        }
                      >
                        {cell.column.id === "select" ? (
                          <div className={`tour-select-${cell.row.index}`}>
                            <TableCheckbox 
                              email={(cell.row.original.email as string)} 
                              onToggle={onToggle!}
                            />
                          </div>
                        ) : cell.column.id === "group" ? (
                          cell.render("Cell").props.cell.value === " - " ? (
                            <Typography
                              align="center"
                              style={{
                                fontWeight: 600,
                                color: "#000",
                              }}
                            >
                              {" "}
                              {cell.render("Cell")}{" "}
                            </Typography>
                          ) : (
                            <Grid
                              container
                              style={{
                                padding: 5,
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              {cell
                                .render("Cell")
                                .props.cell.value.map((group: unknown) => {
                                  return (
                                    <Grid item style={{ margin: 5 }}>
                                      <Typography
                                        align="center"
                                        style={{
                                          borderRadius: 10,
                                          paddingLeft: 10,
                                          paddingRight: 10,
                                          background: "#40628d",
                                          display: "flex",
                                          fontSize: 12,
                                          fontWeight: 400,

                                          color: "#f7f7f7",
                                        }}
                                      >
                                        {" "}
                                        {group}
                                      </Typography>
                                    </Grid>
                                  );
                                })}
                            </Grid>
                          )
                        ) : cell.render("Cell").props.cell.value === " - " ? (
                          <Typography
                            style={{
                              marginLeft: 40,
                              paddingLeft: 5,
                              fontWeight: 600,
                              color: "#000",
                            }}
                          >
                            {" "}
                            {cell.render("Cell")}{" "}
                          </Typography>
                        ) : (
                          <Typography
                            style={{
                              fontSize: 14,
                              fontWeight: 400,
                              color: "#000",
                            }}
                          >
                            {" "}
                            {cell.render("Cell")}{" "}
                          </Typography>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="pagination-top">
          <div className="-pagination" style={{ marginTop: 5 }}>
            <div className="-previous">
              {canPreviousPage ? (
                <Button
                  size="sm"
                  color="info"
                  onClick={() => {
                    previousPage();
                    handlePageSelect(pageSelect - 1);
                  }}
                  disabled={!canPreviousPage}
                >
                  {t("clients:pagination.previous")}
                </Button>
              ) : (
                false
              )}
            </div>
            <div className="-center">
              <GridContainer className={classes.gridContainer}>
                <GridItem xs={12} sm={6} md={4}>
                  <FormControl
                    fullWidth
                    className={
                      classes.selectFormControl +
                      " " +
                      classes.formControlMargins
                    }
                  >
                    <Select
                      MenuProps={{
                        className: classes.selectMenu,
                      }}
                      classes={{
                        select: classes.select,
                      }}
                      value={pageSelect}
                      onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                        gotoPage(event.target.value as number);
                        handlePageSelect(event.target.value as number);
                      }}
                      inputProps={{
                        name: "pageSelect",
                        id: "page-select",
                      }}
                    >
                      {pageSelectData.map((prop, key) => {
                        return (
                          <MenuItem
                            key={key}
                            classes={{
                              root: classes.selectMenuItem,
                              selected: classes.selectMenuItemSelected,
                            }}
                            value={key}
                          >
                            {t("clients:pagination.page", { number: key + 1 })}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={6} md={4}>
                  <FormControl
                    fullWidth
                    className={
                      classes.selectFormControl +
                      " " +
                      classes.formControlMargins
                    }
                  >
                    <Select
                      MenuProps={{
                        className: classes.selectMenu,
                      }}
                      classes={{
                        select: classes.select,
                      }}
                      value={numberOfRows}
                      onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                        setPageSize(event.target.value as number);
                        setNumberOfRows(event.target.value as number);
                      }}
                      inputProps={{
                        name: "numberOfRows",
                        id: "number-of-rows",
                      }}
                    >
                      {numberOfRowsData.map((prop) => {
                        return (
                          <MenuItem
                            key={prop}
                            classes={{
                              root: classes.selectMenuItem,
                              selected: classes.selectMenuItemSelected,
                            }}
                            value={prop}
                          >
                            {t("clients:pagination.rows", { count: prop })} 
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </GridItem>
              </GridContainer>
            </div>

            <div className="-next">
              {canNextPage ? (
                <Button
                  size="sm"
                  color="info"
                  onClick={() => {
                    nextPage();
                    handlePageSelect(pageSelect + 1);
                  }}
                  disabled={!canNextPage}
                >
                  {t("clients:pagination.nextPage")} 
                </Button>
              ) : (
                false
              )}
            </div>
          </div>
        </div>

        <div className="pagination-bottom"></div>
      </div>
    </>
  );
}

// Define a custom filter filter function!
function filterGreaterThan(
  rows: import("react-table").Row[],
  id: string,
  filterValue: number
) {
  return rows.filter((row: import("react-table").Row) => {
    const rowValue = row.values[id];
    return (rowValue as number) >= filterValue;
  });
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = (val: unknown) => typeof val !== "number";

export default Table;
